import React from "react";

import { mdiTag } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import ProjectAPI from "../../../../api/ProjectAPI";
import { BUTTON_VARIANTS } from "../../../../components/button/Button";
import Select from "../../../../components/form/Select";
import Modal from "../../../../components/modal/Modal";

import type { JSX } from "react";

import type {
  OfferComponent,
  OfferComponentDestination,
} from "../../../../api/ProjectAPI.types";
import type { ModalActions } from "../../../../components/modal/Modal";

interface OffersButtonDestinationOption {
  value: OfferComponentDestination;
  label: string;
}

function ModalHeader(): JSX.Element {
  const { t } = useTranslation("editor-platform");

  return (
    <div className="flex flex-wrap items-center gap-[0.9375rem]">
      <div className="border-border flex rounded-[0.46875rem] border p-[0.46875rem]">
        <Icon path={mdiTag} size={1.25} />
      </div>
      <div className="shrink-0 grow basis-0 text-[1.171875rem] font-semibold">
        {t("offerComponentSettingsModalTitle")}
      </div>
    </div>
  );
}

interface FormValues {
  destination: {
    label?: string;
    value: OfferComponentDestination;
  };
}

interface OfferComponentSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  component?: OfferComponent;
}

function OfferComponentSettingsModal({
  isOpen,
  onClose,
  component,
}: OfferComponentSettingsModalProps): JSX.Element | null {
  const { projectSlug } = useParams<{
    projectSlug: string;
  }>();
  const { t } = useTranslation("editor-platform");

  const updateComponentMutation = useMutation({
    mutationKey: ["updateComponent", component?.uuid],
    mutationFn: async (updatedComponent: OfferComponent) =>
      ProjectAPI.updateComponent({ component: updatedComponent, projectSlug }),
    onSuccess: () => {
      toast.success(t("offerComponentUpdated"));
      onClose();
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const methods = useForm<FormValues>({
    values: {
      destination: { value: component?.params.destination ?? "all" },
    },
  });

  if (!component) {
    return null;
  }

  const handleSubmit = (data: FormValues): void => {
    updateComponentMutation.mutate({
      ...component,
      params: {
        ...component.params,
        destination: data.destination.value,
      },
    });
  };

  const offersButtonDestinationOptionList: OffersButtonDestinationOption[] = [
    { value: "all", label: t("offersButtonDestination.options.all") },
    {
      value: "subscriptions",
      label: t("offersButtonDestination.options.subscriptions"),
    },
    {
      value: "purchases",
      label: t("offersButtonDestination.options.purchases"),
    },
  ];

  const formId = "ComponentSettingsForm";

  const modalActions: ModalActions[] = [
    {
      label: t("confirm"),
      buttonVariant: BUTTON_VARIANTS.PRIMARY,
      isFullWidth: true,
      isCloseAfterAction: false,
      formId,
    },
  ];

  return (
    <Modal
      isDivided
      actions={modalActions}
      header={<ModalHeader />}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <FormProvider
        {...methods} // eslint-disable-line react/jsx-props-no-spreading
      >
        <form id={formId} onSubmit={methods.handleSubmit(handleSubmit)}>
          <Select
            isPortal
            isRequired
            label={t("offersButtonDestination.label")}
            name="destination"
            options={offersButtonDestinationOptionList}
          />
        </form>
      </FormProvider>
    </Modal>
  );
}

export default OfferComponentSettingsModal;
